/**
 * Belo Confetti Animation
 * 
 * This JavaScript file handles the confetti animation that appears when a user adds a product to their cart.
 * It creates a canvas-based animation with colorful particles that burst from the clicked button.
 * 
 * @copyright Belo 2025
 * @license Proprietary - Unauthorized copying of this file is strictly prohibited
 */

class BeloConfetti {
  constructor() {
    this.initialized = false;
    this.canvas = null;
    this.ctx = null;
    this.particles = [];
    this.colors = [
      '#2a4b8d', // Belo blue
      '#e67e22', // Orange accent
      '#27ae60', // Success green
      '#f39c12', // Star yellow
      '#9b59b6'  // Purple for variety
    ];
    this.container = null;
    this.animationFrameId = null;
    this.lastTimestamp = 0;
    
    // Bind methods
    this.init = this.init.bind(this);
    this.createParticles = this.createParticles.bind(this);
    this.animate = this.animate.bind(this);
    this.updateParticles = this.updateParticles.bind(this);
    this.drawParticles = this.drawParticles.bind(this);
    this.triggerConfetti = this.triggerConfetti.bind(this);
    this.getButtonPosition = this.getButtonPosition.bind(this);
    this.cleanup = this.cleanup.bind(this);
    
    // Initialize on DOM content loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.init);
    } else {
      this.init();
    }
  }
  
    /**
     * Initialize the confetti animation
     */
    init() {
      if (this.initialized) return;
      
      // Create container for the canvas
      this.container = document.createElement('div');
      this.container.className = 'confetti-container';
      this.container.style.position = 'fixed';
      this.container.style.top = '0';
      this.container.style.left = '0';
      this.container.style.width = '100%';
      this.container.style.height = '100%';
      this.container.style.pointerEvents = 'none';
      this.container.style.zIndex = '9999';
      this.container.style.overflow = 'hidden';
      document.body.appendChild(this.container);
      
      // Create canvas
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'confetti-canvas';
      this.canvas.style.position = 'absolute';
      this.canvas.style.top = '0';
      this.canvas.style.left = '0';
      this.canvas.style.width = '100%';
      this.canvas.style.height = '100%';
      this.container.appendChild(this.canvas);
      
      // Get context
      this.ctx = this.canvas.getContext('2d');
      
      // Set canvas size
      this.resizeCanvas();
      
      // Add event listener for window resize
      window.addEventListener('resize', this.resizeCanvas.bind(this));
      
      // Add event listeners to increase buttons
      const increaseButtons = document.querySelectorAll('.quantity-increase');
      increaseButtons.forEach(button => {
        button.addEventListener('click', (event) => {
          const input = button.parentElement.querySelector('.quantity-input');
          const currentValue = parseInt(input.value) || 0;
          const newValue = currentValue + 1;
          this.triggerConfetti(event, newValue);
        });
      });
      
      this.initialized = true;
    }
  
    /**
     * Resize the canvas to match the window size
     */
    resizeCanvas() {
      if (!this.canvas) return;
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight;
    }
    
    /**
     * Create confetti particles
     */
    createParticles(x, y, count = 80, intensity = 1) {
      this.particles = [];
      
      const vibrantColors = [
        '#FF1493', '#00BFFF', '#FFD700', '#FF4500', '#32CD32', 
        '#9400D3', '#FF00FF', '#00FF7F', '#FF6347', '#1E90FF'
      ];
      
      const shapeTypes = ['circle', 'square', 'triangle', 'star'];
      
      for (let i = 0; i < count; i++) {
        const size = Math.random() * 8 + 3;
        const color = vibrantColors[Math.floor(Math.random() * vibrantColors.length)];
        const shape = shapeTypes[Math.floor(Math.random() * shapeTypes.length)];
        
        const velocityFactor = intensity * 1.0;
        const angleRange = Math.PI * 0.8;
        const angle = (Math.random() * angleRange) - (angleRange / 2) + (Math.PI / 2 * 3);
        
        const speed = (Math.random() * 7 + 3) * velocityFactor;
        const velocity = {
          x: Math.cos(angle) * speed * (0.5 + Math.random() * 0.5),
          y: Math.sin(angle) * speed * (0.8 + Math.random() * 0.4)
        };
        
        velocity.y -= Math.random() * 4 * intensity;
        
        this.particles.push({
          x, y, size, color, shape, velocity,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 5,
          gravity: 0.2,
          opacity: 1,
          fadeSpeed: Math.random() * 0.02 + 0.005
        });
      }
    }
  
    /**
     * Animate the confetti particles
     */
    animate(timestamp) {
      if (!this.ctx) return;
      
      const deltaTime = timestamp - this.lastTimestamp;
      this.lastTimestamp = timestamp;
      
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      
      this.updateParticles(deltaTime);
      this.drawParticles();
      
      if (this.particles.length > 0 && this.particles.some(p => p.opacity > 0)) {
        this.animationFrameId = requestAnimationFrame(this.animate);
      } else {
        this.cleanup();
      }
    }
    
    /**
     * Update particle positions and properties
     */
    updateParticles(deltaTime) {
      const timeScale = deltaTime / 24;
      
      this.particles.forEach(particle => {
        particle.x += particle.velocity.x * timeScale;
        particle.y += particle.velocity.y * timeScale;
        
        particle.velocity.y += particle.gravity * timeScale;
        
        particle.velocity.x *= 0.99;
        particle.velocity.y *= 0.99;
        
        particle.rotation += particle.rotationSpeed * timeScale;
        
        particle.opacity -= particle.fadeSpeed * timeScale;
        if (particle.opacity < 0) particle.opacity = 0;
      });
      
      this.particles = this.particles.filter(p => p.opacity > 0);
    }
    
    /**
     * Draw particles on the canvas
     */
    drawParticles() {
      this.particles.forEach(particle => {
        this.ctx.save();
        
        this.ctx.globalAlpha = particle.opacity;
        this.ctx.fillStyle = particle.color;
        this.ctx.translate(particle.x, particle.y);
        this.ctx.rotate((particle.rotation * Math.PI) / 180);
        
        if (particle.shape === 'circle') {
          this.ctx.beginPath();
          this.ctx.arc(0, 0, particle.size / 2, 0, Math.PI * 2);
          this.ctx.fill();
        } else if (particle.shape === 'square') {
          this.ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size);
        } else if (particle.shape === 'triangle') {
          this.ctx.beginPath();
          this.ctx.moveTo(0, -particle.size / 2);
          this.ctx.lineTo(particle.size / 2, particle.size / 2);
          this.ctx.lineTo(-particle.size / 2, particle.size / 2);
          this.ctx.closePath();
          this.ctx.fill();
        } else if (particle.shape === 'star') {
          const spikes = 5;
          const outerRadius = particle.size / 2;
          const innerRadius = particle.size / 4;
          
          this.ctx.beginPath();
          for (let i = 0; i < spikes * 2; i++) {
            const radius = i % 2 === 0 ? outerRadius : innerRadius;
            const angle = (Math.PI * 2 * i) / (spikes * 2);
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            
            if (i === 0) {
              this.ctx.moveTo(x, y);
            } else {
              this.ctx.lineTo(x, y);
            }
          }
          this.ctx.closePath();
          this.ctx.fill();
        }
        
        this.ctx.restore();
      });
    }
  
    /**
     * Get the position of a button in viewport coordinates
     */
    getButtonPosition(event) {
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };
    }
    
    /**
     * Trigger the confetti animation
     */
    triggerConfetti(event, currentValue) {
      const position = this.getButtonPosition(event);
      
      let intensity = 1;
      let particleCount = 80;
      
      if (currentValue >= 4) {
        intensity = 3;
        particleCount = 200;
      } else if (currentValue >= 2) {
        intensity = 2;
        particleCount = 150;
      }
      
      const button = event.currentTarget;
      button.style.transition = 'all 0.1s ease';
      button.style.backgroundColor = '#27ae60';
      button.style.color = 'white';
      button.style.transform = 'scale(1.2)';
      
      setTimeout(() => {
        button.style.backgroundColor = '';
        button.style.color = '';
        button.style.transform = '';
      }, 200);
      
      this.createParticles(position.x, position.y, particleCount, intensity);
      
      this.lastTimestamp = performance.now();
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = requestAnimationFrame(this.animate);
    }
    
    /**
     * Clean up after animation
     */
    cleanup() {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

// Initialize the confetti animation
const beloConfetti = new BeloConfetti();
